import { useEffect, useMemo, useState } from 'react';
import { useUserState } from '../../../../../hooks/context/useUserState';
import {
  Form,
  JSONObject,
  StaticNumberControl,
} from '../../../../../types/types';
import type { RequestDataValue } from '../../../../../context/RequestDataContext';
import { formatStaticNumberValue } from './utils/formatStaticNumberValue';
import { resolveFormat } from './utils/resolveFormat';

interface StaticNumberInputProps {
  value: number | null | undefined;
  control: StaticNumberControl;
  // mirrors NumberInput: float values get cs-CZ locale formatting, integers get raw string
  isFloat: boolean;
  form?: Form;
  formData?: any;
  requestData?: RequestDataValue;
}

const StaticNumberInput = ({
  value,
  control,
  isFloat,
  form,
  formData,
  requestData,
}: StaticNumberInputProps) => {
  const {
    state: { defaultCurrencyFormat },
  } = useUserState();

  const [resolvedFormat, setResolvedFormat] = useState<string | undefined>(
    () =>
      control.format && control.format.includes('{{')
        ? undefined
        : control.format
  );

  useEffect(() => {
    // No JSONata work needed (no placeholder, or no form context — covers
    // grid-filter usage). Mirrors ReferenceInput's `form && requestData` guard.
    if (
      !control.format ||
      !control.format.includes('{{') ||
      !form ||
      !formData ||
      !requestData
    ) {
      setResolvedFormat(control.format);
      return;
    }

    // Async-into-sync bridge: resolve JSONata, then commit to state. `cancelled`
    // drops stale resolutions when deps change before the prior resolve finished.
    let cancelled = false;
    const formatExpression = control.format;
    // Context mirrors ReferenceInput string-filter shape: requestData spread,
    // `form` key, and form fields at root — supports both `{{ form.x }}` and `{{ x }}`.
    const ctx = {
      ...requestData,
      form: formData,
      ...formData,
    } as JSONObject;

    resolveFormat(formatExpression, ctx)
      .then((resolved) => {
        if (!cancelled) setResolvedFormat(resolved);
      })
      .catch(() => {
        // JSONata error → keep raw template so the cell still renders something.
        if (!cancelled) setResolvedFormat(formatExpression);
      });

    return () => {
      cancelled = true;
    };
  }, [control.format, form, formData, requestData]);

  const displayValue = useMemo<string>(
    () =>
      formatStaticNumberValue(
        value,
        isFloat,
        resolvedFormat,
        defaultCurrencyFormat
      ),
    [value, isFloat, resolvedFormat, defaultCurrencyFormat]
  );

  return (
    <span className="static-string">
      <span className="static-string-content">{displayValue}</span>
    </span>
  );
};

export default StaticNumberInput;
